import { useState } from 'react';
import AnimatedSection from '../../../components/base/AnimatedSection';

export default function BookingSection() {
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    sessionType: '',
    hours: '',
    specialRequests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // April 2026: starts on Wednesday (day index 3, 0=Sun)
  // Weekends in April 2026: 4(Sat),5(Sun), 11(Sat),12(Sun), 18(Sat),19(Sun), 25(Sat),26(Sun)
  const availableDates = [4, 5, 11, 12, 18, 19, 25, 26];

  const availableTimes = [
    '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM',
    '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM',
    '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM',
    '12:00 AM'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('fullName', formData.fullName);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('phone', formData.phone);
      formDataToSubmit.append('sessionType', formData.sessionType);
      formDataToSubmit.append('hours', formData.hours);
      formDataToSubmit.append('specialRequests', formData.specialRequests);
      formDataToSubmit.append('selectedDate', `April ${selectedDate}, 2026`);
      formDataToSubmit.append('selectedTime', selectedTime);
      if (!isExclusive) {
        formDataToSubmit.append('bookingFee', '$35 non-refundable booking fee acknowledged');
      }

      const response = await fetch('https://readdy.ai/api/form/d6rjjvcddmmni7ck8u40', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ fullName: '', email: '', phone: '', sessionType: '', hours: '', specialRequests: '' });
        setSelectedDate(null);
        setSelectedTime(null);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCalendar = () => {
    const days = [];
    // April 2026 starts on Wednesday (index 3)
    const startDayOfWeek = 3;
    const totalDays = 30;

    // Day headers
    const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    dayHeaders.forEach((d) => {
      days.push(
        <div key={`header-${d}`} className="text-center text-xs font-semibold text-gray-400 py-1">
          {d}
        </div>
      );
    });

    // Empty cells before the 1st
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} />);
    }

    for (let i = 1; i <= totalDays; i++) {
      const isAvailable = availableDates.includes(i);
      const isSelected = selectedDate === i;

      days.push(
        <button
          key={i}
          type="button"
          onClick={() => isAvailable && setSelectedDate(i)}
          disabled={!isAvailable}
          className={`
            aspect-square rounded-lg text-sm font-medium transition-all
            ${!isAvailable
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : isSelected
                ? 'bg-red-600 text-white shadow-lg scale-105'
                : 'bg-white text-gray-900 hover:bg-red-50 border border-gray-200 cursor-pointer'
            }
          `}
        >
          {i}
        </button>
      );
    }
    return days;
  };

  const isRecording = formData.sessionType === 'Recording - $60/hr';
  const isExclusive = formData.sessionType === 'Exclusive Project';

  return (
    <section id="booking" className="py-14 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <AnimatedSection animation="fade-up" className="text-center mb-10 md:mb-16">
          <div className="inline-block px-4 py-2 border border-gray-300 rounded-full text-xs font-semibold text-gray-700 mb-3 md:mb-4">
            BOOK A SESSION
          </div>
          <h2 className="text-3xl md:text-6xl font-bold text-gray-900 mb-3 md:mb-4 leading-tight">
            Let&apos;s Create Something<br />Amazing Together
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
            Schedule a professional recording, mixing, or mastering session. Available hours shown in real-time.
          </p>
        </AnimatedSection>

        {/* Booking Fee Banner — hidden for Exclusive Project */}
        {!isExclusive && (
          <AnimatedSection animation="fade-up" delay={150} className="max-w-6xl mx-auto mb-8">
            <div className="bg-amber-50 border border-amber-300 rounded-2xl px-6 py-4 flex items-start gap-4">
              <div className="w-6 h-6 flex items-center justify-center mt-0.5 shrink-0">
                <i className="ri-error-warning-line text-amber-500 text-xl"></i>
              </div>
              <div>
                <p className="text-amber-800 font-bold text-sm mb-1">
                  $35 Non-Refundable Booking Fee Required
                </p>
                <p className="text-amber-700 text-sm leading-relaxed">
                  A <strong>$35 booking fee</strong> is automatically applied to all sessions to secure your time slot. 
                  This fee is <strong>non-refundable</strong> — if you miss your appointment or cancel without sufficient notice, 
                  the booking fee will be forfeited. Please ensure you can commit to your selected date and time before booking.
                </p>
              </div>
            </div>
          </AnimatedSection>
        )}

        {/* Exclusive Project Banner */}
        {isExclusive && (
          <AnimatedSection animation="fade-up" delay={150} className="max-w-6xl mx-auto mb-8">
            <div className="bg-red-50 border border-red-300 rounded-2xl px-6 py-4 flex items-start gap-4">
              <div className="w-6 h-6 flex items-center justify-center mt-0.5 shrink-0">
                <i className="ri-vip-crown-line text-red-500 text-xl"></i>
              </div>
              <div>
                <p className="text-red-800 font-bold text-sm mb-1">
                  Exclusive Project — No Booking Fee
                </p>
                <p className="text-red-700 text-sm leading-relaxed">
                  Exclusive Project sessions are booked at <strong>no upfront fee</strong>. Secure your session and let's build something great together.
                </p>
              </div>
            </div>
          </AnimatedSection>
        )}

        <div className="grid lg:grid-cols-2 gap-6 md:gap-12 max-w-6xl mx-auto">
          <AnimatedSection animation="slide-left" delay={200}>
            <div className="bg-white rounded-2xl p-5 md:p-8 border border-gray-200 shadow-sm">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Select Date &amp; Time</h3>
              <p className="text-xs text-gray-500 mb-4 md:mb-6">Weekends only &mdash; 10:00 AM to 2:00 AM</p>

              <div className="mb-6 md:mb-8">
                <div className="text-center font-bold text-base md:text-lg text-gray-900 mb-3 md:mb-4">April 2026</div>
                <div className="grid grid-cols-7 gap-1 md:gap-2">
                  {renderCalendar()}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-sm md:text-base text-gray-900 mb-3">Available Time Slots</h4>
                <div className="grid grid-cols-3 gap-1.5 md:gap-2">
                  {availableTimes.map((time) => (
                    <button
                      key={time}
                      type="button"
                      onClick={() => setSelectedTime(time)}
                      className={`
                        px-2 md:px-4 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer
                        ${selectedTime === time
                          ? 'bg-red-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }
                      `}
                    >
                      {time}
                    </button>
                  ))}
                </div>
                {selectedTime && ['12:00 AM'].includes(selectedTime) && (
                  <p className="text-xs text-red-600 mt-2">* This time slot continues into the next day</p>
                )}
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="slide-right" delay={350}>
            <form
              id="booking-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-gray-900 rounded-2xl p-5 md:p-8 shadow-xl"
            >
              <div className="text-xs font-semibold text-gray-400 mb-6 tracking-wider">SESSION DETAILS</div>
              
              <div className="space-y-6">
                <div>
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                    required
                    className="w-full bg-transparent border-b border-gray-700 text-white placeholder-gray-500 py-3 focus:border-red-500 focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="w-full bg-transparent border-b border-gray-700 text-white placeholder-gray-500 py-3 focus:border-red-500 focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    className="w-full bg-transparent border-b border-gray-700 text-white placeholder-gray-500 py-3 focus:border-red-500 focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <select
                    name="sessionType"
                    value={formData.sessionType}
                    onChange={(e) => setFormData({...formData, sessionType: e.target.value, hours: ''})}
                    required
                    className="w-full bg-transparent border-b border-gray-700 text-white py-3 focus:border-red-500 focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="" className="bg-gray-900">Session Type</option>
                    <option value="Recording - $60/hr" className="bg-gray-900">Recording — $60/hr (2 hr minimum)</option>
                    <option value="Mix/Mastering - $100/song" className="bg-gray-900">Mix/Mastering — $100 per Song</option>
                    <option value="Exclusive Project" className="bg-gray-900">Exclusive Project — No Booking Fee</option>
                  </select>
                </div>

                {/* Hours selector — only for Recording */}
                {isRecording && (
                  <div>
                    <select
                      name="hours"
                      value={formData.hours}
                      onChange={(e) => setFormData({...formData, hours: e.target.value})}
                      required
                      className="w-full bg-transparent border-b border-gray-700 text-white py-3 focus:border-red-500 focus:outline-none transition-colors cursor-pointer"
                    >
                      <option value="" className="bg-gray-900">Number of Hours</option>
                      <option value="2 hours" className="bg-gray-900">2 Hours — $120</option>
                      <option value="3 hours" className="bg-gray-900">3 Hours — $180</option>
                      <option value="4 hours" className="bg-gray-900">4 Hours — $240</option>
                      <option value="5 hours" className="bg-gray-900">5 Hours — $300</option>
                      <option value="6 hours" className="bg-gray-900">6 Hours — $360</option>
                      <option value="8 hours" className="bg-gray-900">8 Hours — $480</option>
                    </select>
                    <p className="text-xs text-red-400 mt-2">* Minimum 2 hours required for recording sessions</p>
                  </div>
                )}

                <div>
                  <textarea
                    name="specialRequests"
                    placeholder="Special Requests (Optional)"
                    value={formData.specialRequests}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({...formData, specialRequests: e.target.value});
                      }
                    }}
                    maxLength={500}
                    rows={4}
                    className="w-full bg-transparent border-b border-gray-700 text-white placeholder-gray-500 py-3 focus:border-red-500 focus:outline-none transition-colors resize-none"
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.specialRequests.length}/500
                  </div>
                </div>

                {/* Booking fee summary — hidden for Exclusive Project */}
                {!isExclusive && (
                  <div className="bg-gray-800 rounded-xl px-4 py-3 border border-gray-700">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-400">Booking Fee (non-refundable)</span>
                      <span className="text-white font-bold">$35.00</span>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      By confirming, you acknowledge that the $35 booking fee is non-refundable. 
                      Missing your appointment will result in forfeiture of this fee.
                    </p>
                  </div>
                )}

                {/* Exclusive Project — no fee notice */}
                {isExclusive && (
                  <div className="bg-red-900/30 rounded-xl px-4 py-3 border border-red-700">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-red-300">Booking Fee</span>
                      <span className="text-red-300 font-bold">FREE</span>
                    </div>
                    <p className="text-xs text-red-500 leading-relaxed">
                      No upfront fee required for Exclusive Project sessions.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-white text-gray-900 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting
                    ? 'Submitting...'
                    : isExclusive
                      ? 'Confirm Booking — No Fee'
                      : 'Confirm Booking & Pay $35 Fee'
                  }
                </button>

                {submitStatus === 'success' && (
                  <div className="text-center text-green-400 text-sm">
                    Booking confirmed! You'll receive SMS confirmation shortly.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="text-center text-red-400 text-sm">
                    Something went wrong. Please try again.
                  </div>
                )}

                <p className="text-center text-gray-500 text-sm">
                  You'll receive SMS confirmation
                </p>
              </div>
            </form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}